import { useTranslation } from "react-i18next";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { PAY_OUT_BUTTON_STYLES } from "../tips.constants";
import { TipDistributeAction, TipPaymentStatusPill } from "../tips.styled";
import {
  getTipPaymentStatusIcon,
  getTipPaymentStatusLabel,
  getTipPaymentStatusTone,
} from "./tips.rows.logic";
import type { TipRowPaymentActionProps } from "./tips.rows.types";

export const TipRowPaymentAction = (
  props: TipRowPaymentActionProps,
): JSX.Element | null => {
  const { t } = useTranslation();
  const shouldShowEditDistributionAction =
    props.rowPaymentStatus === null &&
    props.hasPersistedDistribution &&
    props.hasWorkers &&
    props.hasApprovedTimeReports;
  const shouldShowDistributeButton =
    props.rowPaymentStatus === null &&
    !props.hasPersistedDistribution &&
    !props.isReadyForPayout &&
    props.currentAmount > 0;
  const shouldShowDistributeAction =
    shouldShowDistributeButton &&
    props.hasWorkers &&
    props.hasApprovedTimeReports;
  const shouldShowMissingTimeReportsAction =
    shouldShowDistributeButton &&
    props.hasWorkers &&
    !props.hasApprovedTimeReports;
  const shouldShowDisabledDistributeAction =
    shouldShowDistributeButton && !props.hasWorkers;
  const shouldShowPayoutAction =
    props.rowPaymentStatus === null && props.isReadyForPayout;

  if (
    !shouldShowPayoutAction &&
    !shouldShowEditDistributionAction &&
    !shouldShowDistributeAction &&
    !shouldShowMissingTimeReportsAction &&
    !shouldShowDisabledDistributeAction &&
    props.rowPaymentStatus === null
  ) {
    return null;
  }

  if (props.rowPaymentStatus !== null) {
    const tone = getTipPaymentStatusTone(props.rowPaymentStatus);
    const iconColor =
      tone === "success"
        ? ColorV2.PositiveGreen
        : tone === "warning"
          ? ColorV2.DarkAmbigiousYellow
          : ColorV2.NegativeRed;

    return (
      <TipDistributeAction>
        <TipPaymentStatusPill $tone={tone}>
          <CustomIcon
            name={getTipPaymentStatusIcon(props.rowPaymentStatus)}
            size="18px"
            color={iconColor}
          />
          {getTipPaymentStatusLabel({
            status: props.rowPaymentStatus,
            t,
          })}
        </TipPaymentStatusPill>
      </TipDistributeAction>
    );
  }

  if (shouldShowPayoutAction) {
    return (
      <TipDistributeAction>
        {shouldShowEditDistributionAction && (
          <SecondaryButton
            aria-label={t("Tips.EditDistribution")}
            iconName="v2/pen"
            label={t("Tips.EditDistribution")}
            onClick={() =>
              props.onDistribute(props.tip.companyTipId, props.currentAmount)
            }
            size="small"
            type="button"
          />
        )}
        <PrimaryButton
          aria-label={t("Tips.PayOut")}
          iconName="v2/send"
          label={t("Tips.PayOut")}
          onClick={() => props.onOpenPayout(props.tip)}
          size="small"
          styleOverrides={PAY_OUT_BUTTON_STYLES}
          type="button"
        />
      </TipDistributeAction>
    );
  }

  return (
    <TipDistributeAction>
      {shouldShowEditDistributionAction ? (
        <SecondaryButton
          aria-label={t("Tips.EditDistribution")}
          iconName="v2/pen"
          label={t("Tips.EditDistribution")}
          onClick={() =>
            props.onDistribute(props.tip.companyTipId, props.currentAmount)
          }
          size="small"
          type="button"
        />
      ) : shouldShowDisabledDistributeAction ? (
        <TooltipHover text={t("CompanyTip.NoWorkers")} arrow="down">
          <PrimaryButton
            aria-label={t("Tips.Distribute")}
            disabled
            iconName="v2/percent"
            label={t("Tips.Distribute")}
            size="small"
            type="button"
          />
        </TooltipHover>
      ) : shouldShowMissingTimeReportsAction ? (
        <TooltipHover text={t("TimeReports.NoTimeReports")} arrow="down">
          <PrimaryButton
            aria-label={t("Tips.Distribute")}
            disabled
            iconName="v2/percent"
            label={t("Tips.Distribute")}
            size="small"
            type="button"
          />
        </TooltipHover>
      ) : (
        <PrimaryButton
          aria-label={t("Tips.Distribute")}
          iconName="v2/percent"
          label={t("Tips.Distribute")}
          onClick={() =>
            props.onDistribute(props.tip.companyTipId, props.currentAmount)
          }
          size="small"
          type="button"
        />
      )}
    </TipDistributeAction>
  );
};
